import React, { FC, useContext, useCallback } from "react";
import Image from "next/image";
import { ZipCodeContext } from "context/zipCode";
import { CartContext } from "context/cart";
import { UserContext } from "context/user";
import { InternalLink } from "@pepdirect/ui/InternalLink";
import { BundleTileLayout } from "@pepdirect/ui/layout/BundleTileLayout";
import { AddToCartButtons } from "components/AddToCartButtons";
import { Price } from "components/Price";
import {
  logProductTileBuyButtonClickEvent,
  logProductClickedEvent,
} from "@pepdirect/helpers/analyticsLogger";
import { getCloudflareLoader } from "@pepdirect/helpers/cloudflareImages";
import { Product } from "@pepdirect/shared/types";
import { endpoints } from "config";
import st from "./bundleTile.module.scss";

interface BundleTileProps {
  item: Product;
  collection?: string;
  imageWidth?: number;
  isInRecommendedSection?: boolean;
}

export const BundleTile: FC<BundleTileProps> = ({
  item,
  imageWidth,
  collection,
  isInRecommendedSection = false,
}) => {
  const { cart, updateCartItem } = useContext(CartContext);
  const { currentUserId } = useContext(UserContext);
  const { blockedItems } = useContext(ZipCodeContext);

  const {
    itemTitle,
    compareAtPrice,
    itemPrice,
    _id,
    isOutOfStock,
    itemSlug,
    itemImgUrl,
    inventoryQty,
    minOrderQty,
  } = item;

  const isItemUnavailable = useCallback(
    (id: string) => {
      return blockedItems.includes(id);
    },
    [blockedItems]
  );

  const cartItem = cart?.items?.find((item) => item.product.id === _id);
  const quantity = cartItem?.quantity || 0;
  const queryParams = new URLSearchParams();
  collection && queryParams.append("collection", collection);
  const internalUrl = `/product/${itemSlug}?${queryParams.toString()}`;

  return (
    <BundleTileLayout isInRecommendedSection={isInRecommendedSection}>
      <InternalLink
        href={internalUrl}
        onClick={() => logProductClickedEvent(currentUserId, item as Product)}
      >
        <Image
          className={st.image}
          src={itemImgUrl || ""}
          width={imageWidth || 275}
          height={imageWidth || 275}
          loader={getCloudflareLoader(endpoints.productsImgCdn)}
          alt={itemTitle ? itemTitle : ""}
        />
        <h2 className={st.title}>{itemTitle}</h2>
      </InternalLink>
      {/* TODO: add library with truncation if needed */}
      <Price
        original={compareAtPrice}
        current={itemPrice}
        isInCents={!isInRecommendedSection}
      />
      <div className={st.add}>
        <AddToCartButtons
          quantity={quantity}
          onChange={(quantity: number) => {
            logProductTileBuyButtonClickEvent(
              currentUserId,
              item as Product,
              quantity
            );
            return updateCartItem({
              productId: _id,
              quantity,
              subscriptionIntervalInDays:
                cartItem?.subscriptionIntervalInDays || null,
            });
          }}
          outOfStock={!!isOutOfStock}
          unavailable={isItemUnavailable(_id)}
          inventoryQty={inventoryQty || 0}
          minOrderQty={minOrderQty || 0}
        />
      </div>
    </BundleTileLayout>
  );
};
